import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Check, CreditCard, Sparkles, Zap, Shield } from 'lucide-react';
import UserLayout from '../components/UserLayout';
import { apiClient } from '../api/api';
import './SubscriptionPage.css';

// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SubscriptionPage = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                // For dummy testing, we define them here
                setPlans([
                    { 
                        id: 'price_free_dummy', // Kept as dummy for now as it's 0
                        name: 'Free', 
                        price: 0, 
                        features: ['1 Shop Connection', 'Basic Analytics', 'Standard Support'],
                        icon: Sparkles,
                        color: '#666',
                        popular: false
                    },
                    { 
                        id: 'price_1Sx3GmFIyUKsjtgHjNVupPcC', 
                        name: 'Basic', 
                        price: 10, 
                        features: ['3 Shop Connections', 'Advanced Analytics', 'Email Alerts', 'Weekly Reports'],
                        icon: Zap,
                        color: '#3b82f6',
                        popular: true
                    },
                    { 
                        id: 'price_1Sx3HlFIyUKsjtgHVeJMC4Hv', 
                        name: 'Pro', 
                        price: 29, 
                        features: ['Unlimited Shops', 'Priority Support', 'Real-time Alerts', 'Custom Reports', 'API Access'],
                        icon: Shield,
                        color: '#a855f7',
                        popular: false
                    },
                ]);
            } catch (error) {
                console.error("Error fetching plans:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchPlans();
    }, []);

    const handleSubscribe = async (priceId) => {
        try {
            const userId = localStorage.getItem('userId');
            if (!userId) {
                alert("Please log in to subscribe.");
                return;
            }
            const response = await apiClient.post('/payments/create-checkout-session', {
                price_id: priceId,
                success_url: window.location.origin + '/payment-success',
                cancel_url: window.location.origin + '/subscription',
            }, {
                params: { user_id: userId }
            });

            const { checkout_url } = response.data;
            if (checkout_url) {
                window.location.href = checkout_url;
            }
        } catch (error) {
            console.error("Payment Error:", error);
            alert("Failed to start checkout. Check console.");
        }
    };

    return (
        <UserLayout>
            <div className="page-relative-wrapper">
                <div className="dashboard-container subscription-page">
                    <div className="subscription-header">
                        <h1 className="subscription-title">
                            <CreditCard size={28} className="title-icon" /> Choose Your Plan
                        </h1>
                        <p className="subscription-subtitle">Unlock the full potential of your shop analytics</p>
                    </div>
                    
                    {loading ? (
                        <div className="loading-state">Loading plans...</div>
                    ) : (
                        <div className="plans-grid">
                            {plans.map(plan => {
                                const Icon = plan.icon;
                                return (
                                    <div key={plan.id} className={`plan-card ${plan.popular ? 'popular' : ''}`}>
                                        {plan.popular && <div className="popular-badge">Most Popular</div>}
                                        <div className="plan-header">
                                            <div className="icon-wrapper" style={{ backgroundColor: `${plan.color}20` }}>
                                                <Icon size={24} color={plan.color} />
                                            </div>
                                            <h2>{plan.name}</h2>
                                        </div>
                                        <div className="price-section">
                                            <span className="currency">$</span>
                                            <span className="amount">{plan.price}</span>
                                            <span className="period">/mo</span>
                                        </div>
                                        <ul className="features-list">
                                            {plan.features.map((f, i) => (
                                                <li key={i}>
                                                    <Check size={16} className="check-icon" />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <button 
                                            onClick={() => handleSubscribe(plan.id)}
                                            className={`subscribe-btn ${plan.price === 0 ? 'secondary' : 'primary'}`}
                                            // style={{ backgroundColor: plan.price > 0 ? plan.color : undefined }}
                                        >
                                            {plan.price === 0 ? 'Current Plan' : 'Subscribe Now'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </UserLayout>
    );
};

export default SubscriptionPage;
